import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { supabase } from "../supaBaseClient.ts";
import type { Profile, User } from "../types/index.ts";

interface AuthProps {
  user: User | null;
  profile: Profile | null;
  onLogin: (_user: User) => void;
  onLogout: () => void;
  onEditName?: () => void;
}

const FONT =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const Auth: React.FC<AuthProps> = ({ user, profile, onLogout, onEditName }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSent(false);

    // If the user arrived via a share-link (?join=CODE), App.tsx persisted
    // the code to localStorage. Forward it to the magic-link redirect URL so
    // the join survives even if localStorage gets wiped between tab opens.
    // The bare origin works as a fallback when no code is pending.
    let redirectTo = globalThis.location.origin;
    try {
      const pending = localStorage.getItem("rankbet_pending_join");
      if (pending) {
        redirectTo += `/?join=${encodeURIComponent(pending)}`;
      }
    } catch {
      // localStorage might be blocked — fine, use plain origin
    }

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: redirectTo,
          shouldCreateUser: true, // also signs up new emails on first login
        },
      });
      if (otpError) throw otpError;
      setSent(true);
    } catch (err: unknown) {
      console.error("Magic link error:", err);
      const msg = err instanceof Error ? err.message : t("auth.errors.unknown");
      const translated =
        {
          "Email rate limit exceeded": t("auth.errors.rateLimit"),
          "For security purposes, you can only request this after 60 seconds.":
            t("auth.errors.wait60"),
          "Signups not allowed for otp": t("auth.errors.notAllowed"),
        }[msg] ?? msg;
      setError(translated);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Defensive clear in case signOut hangs (known issue with supabase v2.0)
      localStorage.removeItem("sb-fvuwfonfrllbjqvywula-auth-token");
      sessionStorage.removeItem("sb-fvuwfonfrllbjqvywula-auth-token");
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("signOut timeout")), 2000),
        ),
      ]);
    } catch (err) {
      console.error("Auth: signOut error or timeout:", err);
    }
    try {
      await onLogout();
    } catch (err) {
      console.error("onLogout error:", err);
    }
  };

  // ----- Logged-in: compact pill in the header --------------------------------
  if (user) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          background: "rgba(255, 255, 255, 0.95)",
          padding: "12px 20px",
          borderRadius: "25px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          border: "1px solid rgba(102, 126, 234, 0.2)",
          fontFamily: FONT,
          letterSpacing: "-0.01em",
        }}
      >
        <button
          type="button"
          onClick={onEditName}
          disabled={!onEditName}
          title={onEditName ? t("profile.edit.header") : undefined}
          style={{
            fontSize: "16px",
            color: "#333",
            fontWeight: 600,
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: onEditName ? "pointer" : "default",
            fontFamily: FONT,
            letterSpacing: "-0.01em",
          }}
        >
          {t("nav.hello", {
            name:
              profile?.display_name ||
              user.email?.split("@")[0] ||
              "Spieler",
          })}
        </button>
        <button
          type="button"
          onClick={handleLogout}
          style={{
            background: "rgba(102, 126, 234, 0.8)",
            border: "none",
            borderRadius: "20px",
            padding: "10px 16px",
            color: "white",
            fontSize: "16px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s ease",
            fontFamily: FONT,
            letterSpacing: "-0.01em",
          }}
          onMouseEnter={(e) => {
            const t = e.target as HTMLElement;
            t.style.background = "rgba(102, 126, 234, 1)";
            t.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            const t = e.target as HTMLElement;
            t.style.background = "rgba(102, 126, 234, 0.8)";
            t.style.transform = "translateY(0)";
          }}
        >
          {t("nav.logout")}
        </button>
      </div>
    );
  }

  // ----- Logged-out: inline Magic-Link form -----------------------------------
  // After a successful send, swap the form for a confirmation message so the
  // user doesn't keep mashing the button (60s rate limit per Supabase).
  if (sent) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "10px",
          maxWidth: "420px",
          margin: "0 auto",
          padding: "20px 24px",
          background: "rgba(16, 185, 129, 0.12)",
          border: "1px solid rgba(16, 185, 129, 0.45)",
          borderRadius: "12px",
          color: "white",
          fontFamily: FONT,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "32px" }}>✉️</div>
        <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
          {t("auth.sent.title")}
        </h3>
        <p
          style={{
            margin: 0,
            fontSize: "14px",
            color: "rgba(255,255,255,0.85)",
            lineHeight: 1.5,
          }}
        >
          <Trans i18nKey="auth.sent.body" values={{ email }} />
        </p>
        <button
          type="button"
          onClick={() => {
            setSent(false);
            setEmail("");
          }}
          style={{
            marginTop: "8px",
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "rgba(255,255,255,0.8)",
            padding: "6px 14px",
            borderRadius: "8px",
            fontSize: "13px",
            cursor: "pointer",
            fontFamily: FONT,
          }}
        >
          {t("auth.sent.tryOther")}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSendMagicLink}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        maxWidth: "420px",
        margin: "0 auto",
        fontFamily: FONT,
      }}
    >
      <input
        type="email"
        required
        autoComplete="email"
        placeholder={t("auth.emailPlaceholder")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={loading}
        style={{
          padding: "14px 16px",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: "10px",
          fontSize: "16px",
          fontFamily: FONT,
          background: "rgba(255,255,255,0.08)",
          color: "white",
          outline: "none",
          boxSizing: "border-box",
        }}
      />
      <button
        type="submit"
        disabled={loading || !email.includes("@")}
        style={{
          padding: "14px 24px",
          background:
            loading || !email.includes("@")
              ? "rgba(102, 126, 234, 0.4)"
              : "rgba(102, 126, 234, 1)",
          color: "white",
          border: "none",
          borderRadius: "10px",
          fontSize: "16px",
          fontWeight: 700,
          cursor: loading || !email.includes("@") ? "not-allowed" : "pointer",
          fontFamily: FONT,
          letterSpacing: "-0.01em",
          transition: "background 0.15s ease",
        }}
      >
        {loading ? t("auth.sending") : t("auth.sendMagicLink")}
      </button>
      <p
        style={{
          color: "rgba(255,255,255,0.55)",
          fontSize: "13px",
          textAlign: "center",
          margin: "4px 0 0 0",
          lineHeight: 1.5,
        }}
      >
        {t("auth.hint")}
      </p>
      {error && (
        <div
          style={{
            background: "rgba(239, 68, 68, 0.18)",
            border: "1px solid rgba(239, 68, 68, 0.5)",
            color: "rgba(255, 220, 220, 0.95)",
            padding: "10px 14px",
            borderRadius: "8px",
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}
    </form>
  );
};

export default Auth;
